import type { GlobalThemeOverrides } from "naive-ui"
import { colors } from "./colors"
import { fontFamily } from "./typography"

// Maps design tokens into Naive UI's theme system so admin components
// (buttons, inputs, tables...) match the same palette the landing uses.
// Hover/pressed use the kit's "Dark blue" since the kit has no explicit states.
export const naiveThemeOverrides: GlobalThemeOverrides = {
  common: {
    fontFamily: fontFamily.sans,

    primaryColor: colors.primary.DEFAULT,
    primaryColorHover: colors.primary.dark,
    primaryColorPressed: colors.primary.dark,
    primaryColorSuppl: colors.primary.DEFAULT,

    infoColor: colors.primary.DEFAULT,
    successColor: colors.success.DEFAULT,
    warningColor: colors.warning.DEFAULT,
    errorColor: colors.danger.DEFAULT,

    textColorBase: colors.neutral.black,
    borderColor: colors.secondary.light,
  },

  // Figma "Input forms" (node 379:3800). The field reads lighter than its backdrop,
  // so it only looks right on a tinted surface (the login card is `bg-secondary`) —
  // a white fill with a white border would vanish. See design-system decisions-log.
  Input: {
    borderRadius: "12px",

    color: colors.secondary.light,
    colorFocus: colors.secondary.light,

    textColor: colors.neutral.black,
    placeholderColor: colors.gray.DEFAULT,
    caretColor: colors.primary.DEFAULT,

    border: `1px solid ${colors.neutral.white}`,
    borderHover: `1px solid ${colors.neutral.white}`,
    borderFocus: `1px solid ${colors.primary.DEFAULT}`,
    borderDisabled: `1px solid ${colors.gray.stroke}`,

    textColorDisabled: colors.gray.DEFAULT,
    placeholderColorDisabled: colors.gray.DEFAULT,

    // Heights/font sizes mirror WButton's scale so inputs and buttons align.
    heightLarge: "54px",
    heightMedium: "46px",
    heightSmall: "38px",
    fontSizeLarge: "16px",
    fontSizeMedium: "14px",
    fontSizeSmall: "12px",
  },

  // Select — no Figma component exists; composed from recorded specs so it reads
  // as one system: the closed field mirrors `Input` above, the open panel mirrors
  // the `Dropdown` menu below. Naive styles the field/panel through peers, not
  // through `Select` itself. Focus rings are dropped (`none`) because the raised
  // `shadow-control` elevation lives on `.n-base-selection` in the admin's
  // main.css — a ring on top would double up.
  Select: {
    peers: {
      InternalSelection: {
        borderRadius: "12px",

        color: colors.secondary.light,
        colorActive: colors.secondary.light,

        textColor: colors.neutral.black,
        placeholderColor: colors.gray.DEFAULT,
        arrowColor: colors.gray.DEFAULT,
        caretColor: colors.primary.DEFAULT,

        border: `1px solid ${colors.neutral.white}`,
        borderHover: `1px solid ${colors.neutral.white}`,
        borderActive: `1px solid ${colors.primary.DEFAULT}`,
        borderFocus: `1px solid ${colors.primary.DEFAULT}`,

        boxShadowHover: "none",
        boxShadowActive: "none",
        boxShadowFocus: "none",

        // No `borderDisabled` key exists on this peer — a disabled field keeps
        // the resting border and signals state through the muted text/arrow.
        colorDisabled: colors.secondary.light,
        textColorDisabled: colors.gray.DEFAULT,
        placeholderColorDisabled: colors.gray.DEFAULT,
        arrowColorDisabled: colors.gray.DEFAULT,

        heightSmall: "38px",
        heightMedium: "46px",
        heightLarge: "54px",
        fontSizeSmall: "12px",
        fontSizeMedium: "14px",
        fontSizeLarge: "16px",

        // 16px text inset matches WInput; the right side reserves the arrow.
        paddingSingle: "0 32px 0 16px",
        paddingMultiple: "4px 32px 4px 10px",
      },

      InternalSelectMenu: {
        borderRadius: "12px",
        color: colors.neutral.white,

        optionTextColor: colors.gray.DEFAULT,
        optionTextColorActive: colors.primary.DEFAULT,
        optionTextColorPressed: colors.primary.DEFAULT,
        optionColorPending: colors.secondary.light,
        optionColorActive: colors.secondary.DEFAULT,
        optionColorActivePending: colors.secondary.DEFAULT,
        optionCheckColor: colors.primary.DEFAULT,

        optionHeightMedium: "38px",
        groupHeaderTextColor: colors.gray.DEFAULT,
        actionDividerColor: colors.secondary.light,
      },
    },
  },

  // Tag — the chips inside a `multiple` select are plain `NTag`s (Naive imports
  // the component, so they can't be themed through Select's peers). Default
  // (untyped) tags become secondary chips; typed ones (success/error/warning)
  // keep deriving from the semantic palette.
  Tag: {
    borderRadius: "8px",
    color: colors.secondary.light,
    textColor: colors.primary.DEFAULT,
    border: `1px solid ${colors.secondary.DEFAULT}`,
    closeIconColor: colors.primary.DEFAULT,
    closeIconColorHover: colors.primary.dark,
    closeIconColorPressed: colors.primary.dark,
  },

  // DataTable — no Figma component; composed from the tinted-surface vocabulary
  // the sidebar/dropdown already use. Header is a `secondary.light` band with
  // `gray` 600 labels; rows are white, split by `secondary.light` rules, and
  // hover to the same tint. Naive keeps separate keys for tables inside modals
  // and popovers — set identically so a table never falls back to Naive grays.
  DataTable: {
    borderRadius: "12px",
    borderColor: colors.secondary.light,
    actionDividerColor: colors.secondary.light,

    thColor: colors.secondary.light,
    thColorHover: colors.secondary.DEFAULT,
    thColorSorting: colors.secondary.DEFAULT,
    thTextColor: colors.gray.DEFAULT,
    thFontWeight: "600",
    thButtonColorHover: colors.secondary.DEFAULT,
    thIconColor: colors.gray.DEFAULT,
    thIconColorActive: colors.primary.DEFAULT,

    tdColor: colors.neutral.white,
    tdTextColor: colors.neutral.black,
    tdColorHover: colors.secondary.light,
    tdColorSorting: colors.secondary.light,
    tdColorStriped: colors.secondary.light,

    borderColorModal: colors.secondary.light,
    thColorModal: colors.secondary.light,
    thColorHoverModal: colors.secondary.DEFAULT,
    thColorSortingModal: colors.secondary.DEFAULT,
    tdColorModal: colors.neutral.white,
    tdColorHoverModal: colors.secondary.light,
    tdColorSortingModal: colors.secondary.light,
    tdColorStripedModal: colors.secondary.light,

    borderColorPopover: colors.secondary.light,
    thColorPopover: colors.secondary.light,
    thColorHoverPopover: colors.secondary.DEFAULT,
    thColorSortingPopover: colors.secondary.DEFAULT,
    tdColorPopover: colors.neutral.white,
    tdColorHoverPopover: colors.secondary.light,
    tdColorSortingPopover: colors.secondary.light,
    tdColorStripedPopover: colors.secondary.light,

    // 16px horizontal inset matches the input/select text inset above the table.
    thPaddingMedium: "12px 16px",
    tdPaddingMedium: "12px 16px",
    paginationMargin: "16px 0 0 0",
  },

  // Pagination — no Figma component; items follow WButton: idle = `ghost`
  // (transparent, gray, hover secondary.light + primary), active = `primary`
  // (primary fill, white). Sized to the small control step (38px, 12px radius)
  // so the page-size select beside it sits on the same line.
  Pagination: {
    itemBorderRadius: "12px",
    itemSizeSmall: "38px",
    itemSizeMedium: "38px",
    itemSizeLarge: "46px",
    itemFontSizeSmall: "12px",
    itemFontSizeMedium: "14px",
    itemFontSizeLarge: "16px",
    itemPaddingMedium: "0 8px",

    itemColor: "transparent",
    itemColorHover: colors.secondary.light,
    itemColorPressed: colors.secondary.DEFAULT,
    itemColorActive: colors.primary.DEFAULT,
    itemColorActiveHover: colors.primary.dark,
    itemColorDisabled: "transparent",

    itemBorder: "1px solid transparent",
    itemBorderHover: "1px solid transparent",
    itemBorderPressed: "1px solid transparent",
    itemBorderActive: `1px solid ${colors.primary.DEFAULT}`,
    itemBorderDisabled: "1px solid transparent",

    itemTextColor: colors.gray.DEFAULT,
    itemTextColorHover: colors.primary.DEFAULT,
    itemTextColorPressed: colors.primary.DEFAULT,
    itemTextColorActive: colors.neutral.white,
    itemTextColorDisabled: colors.gray.stroke,

    buttonColor: "transparent",
    buttonColorHover: colors.secondary.light,
    buttonColorPressed: colors.secondary.DEFAULT,
    buttonBorder: "1px solid transparent",
    buttonBorderHover: "1px solid transparent",
    buttonBorderPressed: "1px solid transparent",
    buttonIconColor: colors.gray.DEFAULT,
    buttonIconColorHover: colors.primary.DEFAULT,
    buttonIconColorPressed: colors.primary.DEFAULT,

    jumperTextColor: colors.gray.DEFAULT,
    jumperTextColorDisabled: colors.gray.stroke,
  },

  // Matches NButton to the shared WButton / Figma button (node 373:4137); Naive's
  // ~3px radius and shorter heights otherwise mismatch the inputs beside them.
  // Manrope comes from a global `.n-button { font-ui }` rule — no per-button key.
  Button: {
    borderRadiusTiny: "12px",
    borderRadiusSmall: "12px",
    borderRadiusMedium: "12px",
    borderRadiusLarge: "12px",

    heightSmall: "38px",
    heightMedium: "46px",
    heightLarge: "54px",
    fontSizeSmall: "12px",
    fontSizeMedium: "14px",
    fontSizeLarge: "16px",

    fontWeight: "500",
  },

  // Toggle — Figma "Toggle" (node 263:1607): 44×26 pill, white thumb, off rail
  // gray.track, on rail primary. (Dark-mode variants are skipped — no dark theme.)
  Switch: {
    railColor: colors.gray.track,
    railColorActive: colors.primary.DEFAULT,
    buttonColor: colors.neutral.white,
    railHeightMedium: "26px",
    railWidthMedium: "44px",
    buttonHeightMedium: "22px",
    buttonWidthMedium: "22px",
    buttonWidthPressedMedium: "26px",
  },

  // Checkbox — Figma "Checklist" (node 322:2615): radius 6, checked = primary fill
  // + white mark; unchecked border maps to gray.border (Figma's white border is
  // invisible on the admin's white surfaces).
  Checkbox: {
    borderRadius: "6px",
    colorChecked: colors.primary.DEFAULT,
    checkMarkColor: colors.neutral.white,
    border: `1px solid ${colors.gray.border}`,
    borderChecked: `1px solid ${colors.primary.DEFAULT}`,
    borderFocus: `1px solid ${colors.primary.DEFAULT}`,
  },

  // Radio — Figma "Radios" (node 322:2626): unselected border gray.border,
  // selected ring + dot primary.
  Radio: {
    buttonBorderColor: colors.gray.border,
    buttonBorderColorActive: colors.primary.DEFAULT,
    dotColorActive: colors.primary.DEFAULT,
  },

  // Dropdown — not in the Figma kit; composed from existing tokens so a menu
  // panel reads like the sidebar it hangs off: 12px radius, white surface, and
  // the sidebar's own idle/hover link vocabulary (gray → secondary.light + primary).
  Dropdown: {
    borderRadius: "12px",
    optionTextColor: colors.gray.DEFAULT,
    optionColorHover: colors.secondary.light,
    optionTextColorHover: colors.primary.DEFAULT,
    optionColorActive: colors.secondary.DEFAULT,
    optionTextColorActive: colors.primary.DEFAULT,
    optionTextColorChildActive: colors.primary.DEFAULT,
    color: colors.neutral.white,
    dividerColor: colors.secondary.light,
  },

  // Card / Dialog — Naive defaults to a ~3px radius, which reads sharp next to the
  // admin's own panels (`rounded-2xl`). `NModal preset="card"` renders a Card, so
  // this is what rounds modals too.
  Card: {
    borderRadius: "16px",
  },
  Dialog: {
    borderRadius: "16px",
  },
}
